import React, { useState } from 'react';
import { Phone, MapPin, Star, Heart, ChefHat, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { mockMenuItems, mockTestimonials } from '../utils/mock';

const Home = () => {
  const whatsappNumber = "919175119942";
  const whatsappMessage = "Hi Northern Treat, I would like to place an order.";
  const bestSellerKulfi = "Rose Kulfi"; // Most ordered kulfi
  
  const handleWhatsAppOrder = () => {
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ChefHat className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-primary font-heading">Northern Treat</h1>
              <p className="text-xs text-muted-foreground">Where North Indian Taste Matters</p>
            </div>
          </div>
          <Button 
            onClick={handleWhatsAppOrder}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            <Phone className="w-4 h-4 mr-2" />
            Order Now
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-accent/10 via-white to-accent/5">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Authentic Homemade Food</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight font-heading">
                Authentic North Indian Flavours, Made With Love at Home
              </h2>
              <p className="text-lg text-gray-600">
                Freshly Prepared | Pure Ingredients | Home Delivery in Magarpatta City
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleWhatsAppOrder}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white px-8"
                >
                  Order on WhatsApp
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-primary text-primary hover:bg-primary/5"
                >
                  View Menu
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => document.getElementById('kulfi')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-accent text-accent hover:bg-accent/5"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Best Sellers
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">100+</span> Happy Customers
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl transform rotate-3" />
              <img 
                src="/images/thali/DaalBaatiChurmaThali.png"
                alt="Authentic North Indian Thali"
                className="relative rounded-3xl shadow-2xl w-full object-cover aspect-square"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Special Highlight - Daali Baati */}
      <section className="py-16 px-4 bg-primary text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="/images/thali/DaalBaatiChurmaThali.png"
                alt="Daali Baati Special"
                className="rounded-2xl shadow-2xl w-full object-contain"
              />
            </div>
            <div className="order-1 md:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 bg-accent px-4 py-2 rounded-full">
                <Heart className="w-4 h-4 text-white" />
                <span className="text-sm font-bold text-white">Most Loved Thali</span>
              </div>
              <h3 className="text-4xl font-bold font-heading">Daali Baati Special</h3>
              <p className="text-xl text-white/90">
                Daal Baati Churma + Gatte Ki Sabji Thali
              </p>
              <p className="text-lg text-white/80">
                Authentic Rajasthani flavours served fresh. Experience the traditional taste of perfectly baked baatis with aromatic daal and sweet churma.
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-accent">₹249</span>
                <span className="text-white/70">per plate</span>
              </div>
              <Button 
                onClick={handleWhatsAppOrder}
                size="lg"
                className="bg-white text-primary hover:bg-white/90"
              >
                Order Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">Our Special Menu</h3>
            <p className="text-lg text-gray-600">Authentic North Indian dishes made with love and care</p>
          </div>
          
          {/* Thali Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h4 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Thali</h4>
              <p className="text-lg text-gray-600">Complete Meal Experience</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {mockMenuItems.thali.map((item, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h5 className="text-xl font-bold text-gray-900 mb-2">{item.name}</h5>
                    <p className="text-2xl font-bold text-primary mb-3">₹{item.price}</p>
                    <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                    <Button 
                      onClick={handleWhatsAppOrder}
                      className="w-full bg-primary hover:bg-primary/90 text-white"
                    >
                      Order Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Sabji Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h4 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Sabji (1 Big Bowl)</h4>
              <p className="text-lg text-gray-600">Freshly Made Vegetable Curries</p>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
              {mockMenuItems.sabji.map((item, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-4 text-center">
                    <h5 className="font-bold text-gray-900 mb-2">{item.name}</h5>
                    <p className="text-lg font-bold text-primary">₹{item.price}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.size}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Button 
                onClick={handleWhatsAppOrder}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white"
              >
                Order Sabji Now
              </Button>
            </div>
          </div>

          {/* Must Try Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h4 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Must Try</h4>
              <p className="text-lg text-gray-600">Special Delicacies</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {mockMenuItems.mustTry.map((item, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-4 text-center">
                    <h5 className="font-bold text-gray-900 mb-2">{item.name}</h5>
                    <p className="text-lg font-bold text-primary">₹{item.price}</p>
                    <p className="text-sm text-gray-500 mt-1">{item.quantity}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Button 
                onClick={handleWhatsAppOrder}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white"
              >
                Order Now
              </Button>
            </div>
          </div>

          {/* Kulfi Section */}
          <div id="kulfi" className="bg-gradient-to-br from-accent/20 to-accent/5 rounded-3xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h4 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Traditional Kulfi</h4>
              <p className="text-lg text-gray-600">Beat the Heat with Homemade Kulfi</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {mockMenuItems.kulfi.map((item, index) => (
                <Card key={index} className={`overflow-hidden hover:shadow-xl transition-shadow ${item.name === bestSellerKulfi ? 'ring-2 ring-accent shadow-lg' : ''}`}>
                  <div className="aspect-square overflow-hidden relative">
                    {item.name === bestSellerKulfi && (
                      <div className="absolute top-2 right-2 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full z-10 flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        Best Seller
                      </div>
                    )}
                    <img 
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-4 text-center">
                    <h5 className="font-bold text-gray-900">{item.name}</h5>
                    <p className="text-lg font-bold text-primary mt-2">₹{item.price}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Button 
                onClick={handleWhatsAppOrder}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white"
              >
                Order Kulfi Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">What Our Customers Say</h3>
          </div>
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-6 min-w-max px-4">
              {mockTestimonials.map((testimonial, index) => (
                <Card key={index} className="w-80 flex-shrink-0 hover:shadow-xl transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <p className="text-gray-700 mb-4 leading-relaxed flex-grow">"{testimonial.text}"</p>
                    <div className="flex items-center gap-3 pt-4 border-t mt-auto">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-primary font-bold">{testimonial.name.charAt(0)}</span>
                      </div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">Made with Love by Trapti</h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              At Northern Treat, every dish is crafted with passion and authenticity. Founded by Trapti, we bring the rich flavors of North India to your doorstep.
            </p>
            <div className="grid md:grid-cols-2 gap-6 text-left mt-8">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                <p className="text-gray-700"><span className="font-semibold">Homemade Hygiene:</span> Prepared in a clean, sanitized home kitchen</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                <p className="text-gray-700"><span className="font-semibold">Authentic Recipes:</span> Traditional North Indian recipes passed through generations</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                <p className="text-gray-700"><span className="font-semibold">Fresh Ingredients:</span> Only the finest, freshest ingredients used daily</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                <p className="text-gray-700"><span className="font-semibold">Passion for Cooking:</span> Every meal made with care and dedication</p>
              </div>
            </div>
            <p className="text-xl font-semibold text-primary italic pt-4">"From Trapti's Kitchen to Your Heart"</p>
          </div>
        </div>
      </section>

      {/* Delivery Information */}
      <section className="py-16 px-4 bg-gradient-to-br from-primary/5 via-white to-accent/5">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">Delivery & Pickup</h3>
            <p className="text-lg text-gray-600">Home Delivery Available Within Magarpatta City</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-8">
                <MapPin className="w-12 h-12 text-primary mb-4" />
                <h4 className="text-2xl font-bold text-gray-900 mb-4">Pickup Location</h4>
                <div className="space-y-2 text-gray-700">
                  <p className="font-semibold">Jasminium Building</p>
                  <p>Magarpatta City, Hadapsar</p>
                  <p>Pune, Maharashtra</p>
                </div>
                <Button 
                  onClick={() => window.open('https://maps.google.com/?q=Jasminium,Magarpatta+City,Hadapsar,Pune', '_blank')}
                  variant="outline"
                  className="mt-6 border-primary text-primary hover:bg-primary/5"
                >
                  Open in Google Maps
                </Button>
              </CardContent>
            </Card>
            <div className="rounded-lg overflow-hidden shadow-lg h-full min-h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3784.4479986841947!2d73.92768931487802!3d18.468498587428776!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2eacf0e0f3ab7%3A0x4b8b8b8b8b8b8b8b!2sMagarpatta%20City%2C%20Hadapsar%2C%20Pune%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Northern Treat Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <ChefHat className="w-8 h-8 text-accent" />
                <div>
                  <h5 className="text-xl font-bold">Northern Treat</h5>
                  <p className="text-sm text-gray-400">Where North Indian Taste Matters</p>
                </div>
              </div>
              <p className="text-gray-400">
                Authentic North Indian homemade food with love and passion.
              </p>
            </div>
            <div>
              <h6 className="text-lg font-bold mb-4">Contact</h6>
              <div className="space-y-2 text-gray-400">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  9175119942
                </p>
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1" />
                  <span>Jasminium Building<br />Magarpatta City, Hadapsar<br />Pune, Maharashtra</span>
                </p>
              </div>
            </div>
            <div>
              <h6 className="text-lg font-bold mb-4">Delivery Area</h6>
              <p className="text-gray-400 mb-4">Magarpatta City and surrounding areas</p>
              <Button 
                onClick={handleWhatsAppOrder}
                className="bg-accent hover:bg-accent/90 text-white"
              >
                <Phone className="w-4 h-4 mr-2" />
                Order on WhatsApp
              </Button>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="mb-2">Tradition Served Fresh</p>
            <p>&copy; 2026 Northern Treat. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={handleWhatsAppOrder}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110 z-50"
        aria-label="Order on WhatsApp"
      >
        <Phone className="w-6 h-6" />
      </button>
    </div>
  );
};

export default Home;
